import type { AppState } from "./state/app-state";
import type { AppStore } from "./state/app-store";
import type { Equation } from "@src/features/equation-editor/equation";
import type { MatrixFor } from "@src/math/types";
import type { PlaybackState } from "@src/features/playback/playback";
import type { RenderState } from "@src/features/visualization/render-state";
import type { VisualizationSource } from "@src/features/visualization/runtime";
import { calculateAnimatedTransform } from "@src/features/playback/animation";
import { getAnimationFrame } from "@src/features/playback/playback";

export function createVisualizationSource(
  store: AppStore,
): VisualizationSource {
  return {
    getRenderState: (now) => {
      const state = store.getState();
      return selectSceneRenderState(state, getFrameNow(state.animation, now));
    },
    isAnimating: () => store.getState().animation.status === "playing",
    subscribe: (listener) => store.subscribe(listener),
    completePlayback: store.getState().completePlayback,
    pausePlayback: store.getState().pausePlayback,
    resetPlayback: store.getState().resetPlayback,
  };
}

export function selectSceneRenderState(
  state: AppState,
  now: number,
): RenderState {
  if (state.activeDimension === 2) {
    return calculate2DRenderState(
      state.equations[2],
      state.appliedTransforms[2],
      state.animation,
      state.showBasis,
      state.showGrid,
      now,
    );
  }
  return calculate3DRenderState(
    state.equations[3],
    state.appliedTransforms[3],
    state.animation,
    state.showBasis,
    state.showGrid,
    now,
  );
}

function calculate2DRenderState(
  equation: Equation<2>,
  appliedTransform: Readonly<MatrixFor<2>>,
  playback: PlaybackState,
  showBasis: boolean,
  showGrid: boolean,
  now: number,
): RenderState<2> {
  const evaluation = equation.evaluation.value;
  return {
    dimension: 2,
    transform: calculateAnimatedTransform(
      evaluation,
      appliedTransform,
      getAnimationFrame(playback, now),
      evaluation.totalTransform,
    ),
    vectors: evaluation.vectors,
    showBasis,
    showGrid,
  };
}

function calculate3DRenderState(
  equation: Equation<3>,
  appliedTransform: Readonly<MatrixFor<3>>,
  playback: PlaybackState,
  showBasis: boolean,
  showGrid: boolean,
  now: number,
): RenderState<3> {
  const evaluation = equation.evaluation.value;
  return {
    dimension: 3,
    transform: calculateAnimatedTransform(
      evaluation,
      appliedTransform,
      getAnimationFrame(playback, now),
      evaluation.totalTransform,
    ),
    vectors: evaluation.vectors,
    showBasis,
    showGrid,
  };
}

function getFrameNow(animation: PlaybackState, now: number): number {
  if (animation.status === "playing" && now < animation.runningSinceMs)
    return animation.runningSinceMs;
  return now;
}
